import { supabase } from "./supabase";

function buildTenantAliasMap(tenants) {
  return tenants.reduce((aliases, tenant, index) => {
    aliases[tenant.full_name] = `Tenant ${index + 1}`;
    return aliases;
  }, {});
}

function buildPropertyAliasMap(properties) {
  return properties.reduce((aliases, property, index) => {
    aliases[property.name] = `Property ${index + 1}`;
    return aliases;
  }, {});
}

function maybeMaskName(value, aliasMap, maskCustomerData) {
  if (!maskCustomerData) return value;
  return aliasMap[value] || "Masked tenant";
}

function maybeMaskProperty(value, aliasMap, highPrivacyMode) {
  if (!highPrivacyMode) return value || "Unassigned property";
  return aliasMap[value] || "Masked property";
}

function maybeMaskUnit(value, highPrivacyMode) {
  if (!highPrivacyMode) return value || "";
  return value ? "Masked unit" : "";
}

function generalizeIssue(issue, highPrivacyMode) {
  if (!highPrivacyMode) return issue;
  return issue ? "Operational issue requires review" : "Maintenance issue";
}

function getDaysPastDue(dueDate) {
  if (!dueDate) return 0;

  const due = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return 0;

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(0, Math.floor((startOfToday.getTime() - due.getTime()) / 86400000));
}

function buildReceivables(
  invoices,
  payments,
  tenantAliasMap,
  propertyAliasMap,
  maskCustomerData,
  highPrivacyMode
) {
  const paidByInvoice = payments.reduce((totalsByInvoice, payment) => {
    if (payment.invoice_id && payment.status === "Paid") {
      totalsByInvoice[payment.invoice_id] =
        (totalsByInvoice[payment.invoice_id] || 0) + Number(payment.amount || 0);
    }

    return totalsByInvoice;
  }, {});

  return invoices
    .map((invoice) => {
      const amount = Number(invoice.amount || 0);
      const paidAmount =
        invoice.status === "Paid" && !paidByInvoice[invoice.id]
          ? amount
          : Number(paidByInvoice[invoice.id] || 0);

      return {
        id: invoice.id,
        tenant: maybeMaskName(invoice.tenant || "Unknown tenant", tenantAliasMap, maskCustomerData),
        property: maybeMaskProperty(invoice.property, propertyAliasMap, highPrivacyMode),
        amount,
        balance: Math.max(amount - paidAmount, 0),
        dueDate: invoice.due_date || "",
        status: invoice.status,
        daysPastDue: getDaysPastDue(invoice.due_date),
      };
    })
    .filter((invoice) => invoice.balance > 0)
    .sort((a, b) => b.daysPastDue - a.daysPastDue || b.balance - a.balance);
}

function buildTopProperties(properties, propertyAliasMap, highPrivacyMode) {
  return [...properties]
    .map((property) => {
      const units = Number(property.units || 0);
      const occupied = Number(property.occupied || 0);
      const occupancy = units > 0 ? Math.round((occupied / units) * 100) : 0;

      return {
        id: property.id,
        name: maybeMaskProperty(property.name, propertyAliasMap, highPrivacyMode),
        location: highPrivacyMode ? "Masked location" : property.location,
        status: property.status,
        occupancy,
        vacantUnits: Math.max(units - occupied, 0),
        openMaintenance: Number(property.maintenance_open || 0),
        monthlyRevenue: Number(property.rent_per_unit || 0) * occupied,
      };
    })
    .sort((a, b) => b.openMaintenance + b.vacantUnits - (a.openMaintenance + a.vacantUnits))
    .slice(0, 12);
}

function buildLeaseSignals(
  tenants,
  tenantAliasMap,
  propertyAliasMap,
  maskCustomerData,
  highPrivacyMode
) {
  const today = new Date();
  const nextMonth = new Date(today);
  nextMonth.setDate(today.getDate() + 45);

  return tenants
    .filter((tenant) => tenant.lease_end)
    .map((tenant) => ({
      id: tenant.id,
      fullName: maybeMaskName(tenant.full_name, tenantAliasMap, maskCustomerData),
      property: maybeMaskProperty(tenant.property_name, propertyAliasMap, highPrivacyMode),
      unit: maybeMaskUnit(tenant.unit, highPrivacyMode),
      status: tenant.status,
      leaseEnd: tenant.lease_end,
    }))
    .filter((tenant) => {
      const leaseEnd = new Date(`${tenant.leaseEnd}T00:00:00`);
      return !Number.isNaN(leaseEnd.getTime()) && leaseEnd <= nextMonth;
    })
    .sort((a, b) => new Date(a.leaseEnd) - new Date(b.leaseEnd))
    .slice(0, 10);
}

function buildTicketSignals(tickets, propertyAliasMap, highPrivacyMode) {
  return tickets
    .filter((ticket) => ticket.status !== "Completed")
    .map((ticket) => ({
      id: ticket.id,
      issue: generalizeIssue(ticket.issue, highPrivacyMode),
      property: maybeMaskProperty(ticket.property, propertyAliasMap, highPrivacyMode),
      unit: maybeMaskUnit(ticket.unit, highPrivacyMode),
      priority: ticket.priority,
      status: ticket.status,
      assigned: highPrivacyMode ? "Assigned team" : ticket.assigned,
      reportedOn: ticket.reported_on,
    }))
    .sort((a, b) => {
      const priorityRank = { High: 3, Medium: 2, Low: 1 };
      return (priorityRank[b.priority] || 0) - (priorityRank[a.priority] || 0);
    })
    .slice(0, 12);
}

export function buildPortfolioAssistantPayload(data, summary, currency, options = {}) {
  const maskCustomerData = options.maskCustomerData ?? true;
  const highPrivacyMode = options.highPrivacyMode ?? false;
  const tenantAliasMap = buildTenantAliasMap(data.tenants || []);
  const propertyAliasMap = buildPropertyAliasMap(data.properties || []);

  return {
    currency,
    privacy: {
      customerDataMasked: maskCustomerData,
      highPrivacyMode,
      mode: options.mode || "masked",
    },
    summary: {
      totalProperties: summary.totalProperties,
      totalTenants: summary.totalTenants,
      portfolioRevenue: summary.portfolioRevenue,
      occupancyRate: summary.occupancyRate,
      collectionsEfficiency: summary.collectionsEfficiency,
      pendingPayments: summary.pendingPayments,
      overduePayments: summary.overduePayments,
      openTicketCount: summary.openTickets.length,
      highPriorityTicketCount: summary.highPriorityTickets.length,
      priorityProperty: summary.priorityProperty
        ? {
            name: maybeMaskProperty(summary.priorityProperty.name, propertyAliasMap, highPrivacyMode),
            location: highPrivacyMode ? "Masked location" : summary.priorityProperty.location,
            status: summary.priorityProperty.status,
            maintenanceOpen: Number(summary.priorityProperty.maintenance_open || 0),
          }
        : null,
    },
    properties: buildTopProperties(data.properties, propertyAliasMap, highPrivacyMode),
    receivables: buildReceivables(
      data.invoices,
      data.payments,
      tenantAliasMap,
      propertyAliasMap,
      maskCustomerData,
      highPrivacyMode
    ).slice(0, 12),
    payments: data.payments
      .filter((payment) => payment.status === "Pending" || payment.status === "Overdue")
      .map((payment) => ({
        id: payment.id,
        tenant: maybeMaskName(payment.tenant_name, tenantAliasMap, maskCustomerData),
        property: maybeMaskProperty(payment.property_name, propertyAliasMap, highPrivacyMode),
        amount: Number(payment.amount || 0),
        dueDate: payment.due_date || "",
        status: payment.status,
      }))
      .slice(0, 12),
    maintenance: buildTicketSignals(data.tickets, propertyAliasMap, highPrivacyMode),
    leaseRisk: buildLeaseSignals(
      data.tenants,
      tenantAliasMap,
      propertyAliasMap,
      maskCustomerData,
      highPrivacyMode
    ),
  };
}

export function inferPortfolioPromptScope(prompt) {
  const normalized = String(prompt || "").toLowerCase();

  if (
    normalized.includes("maintenance") ||
    normalized.includes("ticket") ||
    normalized.includes("repair") ||
    normalized.includes("issue") ||
    normalized.includes("risk")
  ) {
    return "maintenance";
  }

  if (
    normalized.includes("payment") ||
    normalized.includes("rent") ||
    normalized.includes("invoice") ||
    normalized.includes("arrears") ||
    normalized.includes("collection") ||
    normalized.includes("overdue") ||
    normalized.includes("receivable")
  ) {
    return "collections";
  }

  if (
    normalized.includes("lease") ||
    normalized.includes("renewal") ||
    normalized.includes("expiry") ||
    normalized.includes("tenant") ||
    normalized.includes("resident")
  ) {
    return "lease-risk";
  }

  if (
    normalized.includes("property") ||
    normalized.includes("occupancy") ||
    normalized.includes("vacancy") ||
    normalized.includes("portfolio")
  ) {
    return "property-performance";
  }

  return "general";
}

export function minimizePortfolioPayload(portfolio, scope) {
  const base = {
    currency: portfolio.currency,
    privacy: portfolio.privacy,
    summary: portfolio.summary,
    scope,
  };

  if (scope === "maintenance") {
    return {
      ...base,
      includedSections: ["summary", "maintenance", "properties"],
      properties: portfolio.properties,
      maintenance: portfolio.maintenance,
    };
  }

  if (scope === "collections") {
    return {
      ...base,
      includedSections: ["summary", "receivables", "payments"],
      receivables: portfolio.receivables,
      payments: portfolio.payments,
    };
  }

  if (scope === "lease-risk") {
    return {
      ...base,
      includedSections: ["summary", "leaseRisk", "properties"],
      properties: portfolio.properties,
      leaseRisk: portfolio.leaseRisk,
    };
  }

  if (scope === "property-performance") {
    return {
      ...base,
      includedSections: ["summary", "properties", "maintenance"],
      properties: portfolio.properties,
      maintenance: portfolio.maintenance.slice(0, 6),
    };
  }

  return {
    ...base,
    includedSections: ["summary", "properties", "receivables", "maintenance", "leaseRisk"],
    properties: portfolio.properties,
    receivables: portfolio.receivables.slice(0, 6),
    maintenance: portfolio.maintenance.slice(0, 6),
    leaseRisk: portfolio.leaseRisk.slice(0, 6),
  };
}

export async function askPortfolioAssistant({ prompt, portfolio }) {
  const { data, error } = await supabase.functions.invoke("spacem8-ai", {
    body: {
      prompt,
      portfolio,
    },
  });

  if (error) {
    throw new Error(error.message || "SpaceM8 AI could not reach the assistant service.");
  }

  if (!data?.answer) {
    throw new Error("SpaceM8 AI returned an empty response.");
  }

  return data;
}
